import { Badge, SectionCard } from "@/deps/ui-library";
import { useFiscalYears } from "@/api/queries";
import { formatDate } from "@/lib/dates";
import { YearEndMethodCard } from "./YearEndMethodCard";

/** What a fiscal year's status is called on screen. */
const STATUS: Record<string, { label: string; tone: "success" | "warning" | "default" }> = {
  open: { label: "Open", tone: "success" },
  locked: { label: "Locked", tone: "warning" },
  closed: { label: "Closed", tone: "default" },
};

export function PeriodControlTab() {
  // Three hand-typed periods used to sit here, beside a year-end wizard reading the real ones —
  // two answers to which years exist, on one screen.
  const { data: years, isPending } = useFiscalYears();

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <SectionCard title="Fiscal years" flush>
        {isPending ? (
          <p className="text-fg-muted p-4 text-sm">Loading</p>
        ) : (
          <ul className="flex flex-col">
            {(years ?? []).map((year) => (
              <li
                key={year.id}
                className="border-line-soft flex items-center gap-3 border-b px-4 py-2.5 last:border-0"
              >
                <span className="text-fg flex-1 text-sm">
                  {year.label}
                  {year.isCurrent ? (
                    <span className="text-fg-hint ml-2 text-xs">current</span>
                  ) : null}
                </span>
                <span className="text-fg-hint text-xs">
                  {formatDate(year.start)} – {formatDate(year.end)}
                </span>
                <Badge colorScheme={STATUS[year.status]?.tone ?? "default"} size="sm">
                  {STATUS[year.status]?.label ?? year.status}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      <YearEndMethodCard />

      <SectionCard title="What closing means">
        <dl className="flex flex-col gap-3 text-sm">
          <div>
            <dt className="text-fg font-medium">Soft close</dt>
            <dd className="text-fg-muted text-xs">
              Nothing more posts to the period. It can be reopened, and the reopening is recorded
              with a reason — that entry is what an auditor will ask about.
            </dd>
          </div>
          <div>
            <dt className="text-fg font-medium">Hard close</dt>
            <dd className="text-fg-muted text-xs">
              Nothing more posts, and it can't be reopened on its own. The only way back is an
              administrator reopening the year, for chosen months and accounts, with a reason.
            </dd>
          </div>
          <div>
            <dt className="text-fg font-medium">Lock date</dt>
            <dd className="text-fg-muted text-xs">
              Freezes everything up to a day while the rest of the period stays open — how a
              month-end is held while the last few days are still being entered.
            </dd>
          </div>
        </dl>
      </SectionCard>
    </div>
  );
}
